// 👨 NAV — el marco del panel: el fondo (.BGG), la barra lateral (.SBR), el
// bisel (.FRM), la barra de herramientas (.NAV) y la ayuda (.HLP). Se pinta
// UNA vez en el boot (core🫀.js), antes de montar la vista, y vive en TODAS
// las páginas: lo que cambia por página se enseña o se esconde con [hidden]
// (setNavPage), nunca se vuelve a pintar.
//
// La barra de herramientas es del shell, como en 0101sanity/nexus, y tiene
// tres huecos: las herramientas a la izquierda (.NAV_tools), la MIGA en medio
// (.NAV_crumb: DROP / privada · clientes…) y los controles a la derecha
// (.NAV_ctl: orden, modo, la lupa). Las vistas rellenan los huecos con
// setNavTools() y escriben la miga con setCrumb(); la miga se ESCRIBE con el
// scramble de 🔡SCB en vez de aparecer de golpe, sólo en los tramos que
// cambian.
//
// Nada de esto puede vivir DENTRO de la vista: un ancestro con transform
// (los intros de anime) se convierte en el contenedor de todo position:fixed.
//
// API:
//   initNav()                    pinta el chrome, ata la pastilla y los botones
//   setNavPage(page)             'login' | 'browser' | 'settings' — qué se ve
//   setNavActive(key)            marca .A en el .NAV_link[data-key] (la pastilla)
//   setNavTools({ left, right }) HTML de los dos huecos de la barra
//   setCrumb(segments)           [{ label, href? }] — la miga; href = clicable
//   [data-crumb-count]           el contador al final de la miga (lo escribe 📊GRD)
//
// Lo que hace cada botón sale por el bus; la vista montada decide:
//   nav:section  {key, href}  archivos / ajustes (los <a> del rail)
//   nav:crumb    {href}       un tramo clicable de la miga
//   search:focus              la lupa del rail (⌘K hace lo mismo desde 🔍SRC)
// Salir y el tema no pasan por la vista: salir confirma y recarga desde
// aquí; el tema lo gobierna 🌗THM con su listener delegado.

import './👨NAV.pcss'
import { animate, swiftOut } from '@anm/🪇ANM.js'
import { bus } from '@js/🚌BUS.js'
import { ICO } from '@dna/🎨ICO.js'
import { escribirSCB } from '@js/🔡SCB.js'

let sbr = null
let nav = null
let indicator = null
let observer = null
let page = 'login'
let crumbSegs = []

// Duración (en SEGUNDOS: engine.timeUnit = 's') con la que se escribe un
// tramo de la miga. Es la de la entrada de una vista (core🫀.js): la miga
// corre AL LADO de esa coreografía, no detrás.
const CRUMB_S = .22

const ICO_FOLDER_PLUS = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"/><path d="M12 10.5v5M9.5 13h5"/></svg>`
const ICO_FILES = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"/><path d="M3 11h18"/></svg>`

export { ICO_FOLDER_PLUS }

export function initNav() {
  if (sbr) return
  if (!document.querySelector('.BGG')) {
    document.body.insertAdjacentHTML('afterbegin', chromeTemplate())
  }
  sbr = document.querySelector('.SBR')
  nav = document.querySelector('.NAV')
  indicator = sbr?.querySelector('.SBR_ind')
  if (!sbr || !indicator || !nav) {
    console.error('[drop-killer] NAV: falta .SBR/.SBR_ind/.NAV')
    return
  }

  // La pastilla sigue a la sección activa: un MutationObserver sobre las
  // clases de los enlaces mueve la pastilla cada vez que cambia el .A.
  observer = new MutationObserver(() => moveIndicator({ animate: true }))
  sbr.querySelectorAll('.NAV_link').forEach(link => {
    observer.observe(link, { attributes: true, attributeFilter: ['class', 'hidden'] })
  })
  window.addEventListener('resize', () => moveIndicator({ animate: false }), { passive: true })
  requestAnimationFrame(() => moveIndicator({ animate: false }))

  bind()
}

export function setNavPage(next) {
  page = next
  if (!sbr) return
  const login = page === 'login'
  sbr.querySelector('.SBR_links').hidden = login
  sbr.querySelectorAll('[data-action="connect"], [data-action="logout"], [data-key="settings"]')
    .forEach(el => { el.hidden = login })
  if (login) setNavActive(null)
  // Sin vista con herramientas, los huecos se vacían
  if (page !== 'browser') setNavTools({ left: '', right: '' })
}

export function setNavActive(key) {
  if (!sbr) return
  sbr.querySelectorAll('.NAV_link').forEach(link => {
    link.classList.toggle('A', !!key && link.dataset.key === key)
    if (link.tagName === 'A') {
      if (key && link.dataset.key === key) link.setAttribute('aria-current', 'page')
      else link.removeAttribute('aria-current')
    }
  })
}

export function setNavTools({ left = '', right = '' } = {}) {
  if (!nav) return
  const l = nav.querySelector('[data-nav-left]')
  const r = nav.querySelector('[data-nav-right]')
  l.innerHTML = left
  r.innerHTML = right
  l.hidden = !left
  r.hidden = !right
}

// ─── La miga ───────────────────────────────────────────────────────
// DROP / tramo · tramo · tramo. Cada tramo con href es un botón (navega por el
// bus); el último es donde estás. Sólo se escriben con el scramble los tramos
// que cambian respecto a la miga anterior: repetir el ruido en lo que no se
// ha movido sería un tic, no una señal.

export function setCrumb(segments = []) {
  if (!nav) return
  const host = nav.querySelector('[data-crumb-segs]')
  const prev = crumbSegs
  crumbSegs = segments.map(s => ({ label: String(s.label ?? ''), href: s.href || null }))

  host.innerHTML = ''
  crumbSegs.forEach((seg, i) => {
    if (i > 0) {
      const dot = document.createElement('i')
      dot.textContent = '·'
      host.append(dot)
    }
    const last = i === crumbSegs.length - 1
    const el = document.createElement(seg.href && !last ? 'button' : 'span')
    el.className = 'NAV_seg' + (last ? ' NAV_seg--here' : '')
    if (el.tagName === 'BUTTON') {
      el.type = 'button'
      el.dataset.href = seg.href
      el.dataset.tip = `Vuelve a ${seg.label}.`
      el.dataset.key = 'miga'
    }
    const changed = prev[i]?.label !== seg.label
    // El texto final ya está puesto (por si no hay animación); el scramble
    // lo reescribe desde la izquierda sobre ese mismo nodo.
    el.textContent = seg.label
    host.append(el)
    if (changed) escribirSCB(el, seg.label, CRUMB_S)
  })
}

// ─── El chrome ─────────────────────────────────────────────────────

function chromeTemplate() {
  const link = (key, ico, label, tip, href) =>
    `<a class="NAV_link SBR_btn" href="${href}" data-key="${key}" data-tip="${tip}">${ico}<span class="SBR_lbl">${label}</span></a>`
  const act = (action, ico, label, tip, cls = '') =>
    `<button class="SBR_btn ${cls}" type="button" data-action="${action}" data-tip="${tip}" aria-label="${label}">${ico}<span class="SBR_lbl">${label}</span></button>`

  return `
    <a class="SKIP" href="#main">Ir al contenido</a>

    <div class="BGG" aria-hidden="true">
      <div class="BGG_blob"></div>
      <div class="BGG_grain"></div>
    </div>

    <nav class="SBR" aria-label="Secciones">
      <a class="SBR_logo" href="#/private" aria-label="drop-killer" data-key="drop" data-tip="drop-killer: tus archivos en drop.csskiller.com. Vuelve al principio.">${ICO.logoBrand}</a>
      <span class="SBR_ind" aria-hidden="true"></span>

      <div class="SBR_links">
        ${link('files', ICO_FILES, 'archivos', 'Los archivos: la carpeta privada y la pública, con el árbol a la izquierda y lo que hay dentro a la derecha.', '#/private')}
        ${act('search', ICO.search, 'buscar', 'Busca por nombre en todo el drop, privado y público. Tecla ⌘K; Esc limpia.')}
        ${act('help', ICO.help, 'atajos', 'Los atajos de teclado del panel. Tecla ?')}
      </div>

      <div class="SBR_foot">
        ${act('connect', ICO.plug, 'SFTP', 'Monta el drop en el Finder por SFTP: Mountain Duck, Cyberduck o «csk drop mount». Con los datos y los pasos.')}
        ${link('settings', ICO.cog, 'ajustes', 'Ajustes: los dispositivos (passkeys) que pueden abrir este panel — añadir, renombrar, revocar.', '#/settings/devices')}
        <button class="SBR_btn THM" type="button" data-action="theme-toggle" aria-label="Cambiar tema">
          <svg class="THM_ico" viewBox="0 0 24 24" aria-hidden="true">
            <mask id="thm-mask">
              <rect x="0" y="0" width="24" height="24" fill="#fff"/>
              <circle class="THM_moon" cx="24" cy="10" r="6" fill="#000"/>
            </mask>
            <circle class="THM_sun" cx="12" cy="12" r="7" mask="url(#thm-mask)"/>
            <g class="THM_rays" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
              <line x1="12" y1="1.5" x2="12" y2="3.5"/>
              <line x1="12" y1="20.5" x2="12" y2="22.5"/>
              <line x1="1.5" y1="12" x2="3.5" y2="12"/>
              <line x1="20.5" y1="12" x2="22.5" y2="12"/>
              <line x1="4.6" y1="4.6" x2="6" y2="6"/>
              <line x1="18" y1="18" x2="19.4" y2="19.4"/>
              <line x1="4.6" y1="19.4" x2="6" y2="18"/>
              <line x1="18" y1="6" x2="19.4" y2="4.6"/>
            </g>
          </svg>
          <span class="SBR_lbl">tema</span>
        </button>
        ${act('logout', ICO.logout, 'salir', 'Cierra la sesión en este navegador. El passkey sigue en tu llavero: vuelves a entrar con Touch ID.', 'SBR_btn--danger')}
      </div>
    </nav>

    <div class="FRM" aria-hidden="true"></div>

    <nav class="NAV" aria-label="Herramientas">
      <div class="NAV_bar">
        <div class="NAV_tools" data-nav-left hidden></div>
        <div class="NAV_crumb" aria-label="Dónde estás">
          <b>DROP</b><i>/</i><span class="NAV_segs" data-crumb-segs></span><span class="NAV_count" data-crumb-count hidden></span>
        </div>
        <div class="NAV_ctl" data-nav-right hidden></div>
      </div>
    </nav>

    <div class="HLP" id="help" role="status" aria-live="polite">
      <div class="HLP_inner">
        <div class="HLP_key" id="help-key"></div>
        <div class="HLP_txt" id="help-txt"></div>
      </div>
    </div>
  `
}

// ─── Los botones ───────────────────────────────────────────────────

function bind() {
  sbr.addEventListener('click', (e) => {
    const link = e.target.closest('.NAV_link, .SBR_logo')
    if (link) {
      e.preventDefault()
      const key = link.dataset.key === 'drop' ? 'files' : link.dataset.key
      bus.emit('nav:section', { key, href: link.getAttribute('href') })
      return
    }
    const btn = e.target.closest('[data-action]')
    if (!btn) return
    switch (btn.dataset.action) {
      case 'search':  return bus.emit('search:focus')
      case 'help':    return showShortcuts()
      case 'connect': return openConnection()
      case 'logout':  return logout()
      // theme-toggle: lo atiende 🌗THM.js (listener delegado en document)
    }
  })

  // Un tramo de la miga: navega quien tenga la vista
  nav.addEventListener('click', (e) => {
    const seg = e.target.closest('.NAV_seg[data-href]')
    if (!seg) return
    bus.emit('nav:crumb', { href: seg.dataset.href })
  })
}

async function openConnection() {
  const con = await import('@comps/🔌CON/🔌CON.js')
  await con.initCON()
  bus.emit('connection:open')
}

async function logout() {
  const [{ MDL }, { CAL }] = await Promise.all([
    import('@comps/🪟MDL/🪟MDL.js'),
    import('@js/☎️CAL.js'),
  ])
  const ok = await MDL.confirm('Salir · drop-killer', {
    body: 'Se cierra la sesión en este navegador. Para volver a entrar, el passkey.',
    ok: 'Salir',
    kind: 'danger',
  })
  if (!ok) return
  try { await CAL.logout() } catch { /* la cookie ya no vale: recargar lleva al login igual */ }
  const view = document.querySelector('[data-view="app"]')
  animate(view || document.body, {
    opacity: [1, 0],
    duration: .2,
    ease: swiftOut,
    onComplete: () => { window.location.hash = ''; window.location.reload() },
  })
}

// ─── El cheat sheet de atajos ──────────────────────────────────────

export async function showShortcuts() {
  const { MDL } = await import('@comps/🪟MDL/🪟MDL.js')
  const rows = [
    ['Moverse',           ''],
    ['Flechas',           'Mover la selección'],
    ['Intro',             'Abrir la carpeta o el archivo'],
    ['Espacio',           'Vista previa'],
    ['Esc',               'Cerrar menú, modal o vista previa'],

    ['Seleccionar',       ''],
    ['Mayús + clic',      'Seleccionar un rango'],
    ['⌘/Ctrl + clic',     'Añadir o quitar uno'],
    ['⌘/Ctrl + A',        'Seleccionar todo'],
    ['⌘/Ctrl + D',        'Deseleccionar todo'],

    ['Acciones',          ''],
    ['R',                 'Renombrar lo seleccionado'],
    ['Supr / Retroceso',  'Borrar lo seleccionado'],
    ['⌘/Ctrl + K',        'Buscar archivos'],

    ['Arrastrar',         ''],
    ['Arrastrar fila',    'Mover a una carpeta o a una rama del árbol'],
    ['Soltar en el árbol','Mover la selección allí'],

    ['Ayuda',             ''],
    ['?',                 'Esta chuleta'],
  ]

  const frag = document.createElement('div')
  frag.innerHTML = `
    <dl class="SHC">
      ${rows.map(([k, v]) => v === ''
        ? `<dt class="SHC_head">${esc(k)}</dt>`
        : `<dt class="SHC_key"><kbd>${esc(k)}</kbd></dt><dd class="SHC_val">${esc(v)}</dd>`
      ).join('')}
    </dl>
  `
  MDL.open({
    title: 'Atajos · teclado',
    body: frag,
    actions: [{ label: 'Cerrar', kind: 'primary', value: true }],
  })
}

// ─── La pastilla ───────────────────────────────────────────────────

function moveIndicator({ animate: anim }) {
  if (!indicator || !sbr) return
  const active = sbr.querySelector('.NAV_link.A:not([hidden])')
  if (!active) { indicator.style.opacity = '0'; return }

  const wrap = sbr.getBoundingClientRect()
  const r = active.getBoundingClientRect()
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  indicator.style.transition = (anim && !reduce) ? '' : 'none'
  indicator.style.transform = `translate(${r.left - wrap.left}px, ${r.top - wrap.top}px)`
  indicator.style.opacity = '1'
  if (!anim) {
    // fuerza el estilo sin transición y devuelve la transición del CSS
    void indicator.offsetWidth
    indicator.style.transition = ''
  }
}

function esc(s) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}
